/* eslint-disable react-refresh/only-export-components -- shared Webster-builder helpers are intentionally colocated with the component. */
import { useState } from 'preact/hooks'
import { Save, Loader2, Globe, ExternalLink } from 'lucide-preact'
import type { WebsterAssetType } from '@/hooks/useWebsterBuilder'
import type { AssetVersionViewDto } from '@/api/asset'

// ---------------------------------------------------------------------------
// Save-as-Version state derivation
// ---------------------------------------------------------------------------

export interface SaveAsVersionStateInput {
    assetType: WebsterAssetType
    lpReadyForVersion: boolean
    /** Bound email template id on the current draft. Email Copy can't be
     *  promoted to a version without one — the rendered email needs the
     *  template's slot frame. Null/empty for non-Email-Copy or unbound copy. */
    emailCopyTemplateAssetId: string | null
    versions: AssetVersionViewDto[]
    currentVersionUploadedAt: string | null
    draftUpdatedAt: string | null
    /** True while the active builder reports generation in flight — either the
     *  initial auto-generation OR a chat-driven rebuild (bubbled up from
     *  LandingPageBuilder / EmailBuilder). The live in-memory signal. */
    generating: boolean
    /** Server-persisted generation status off the WebAsset ('Generating' |
     *  'Completed' | 'Failed' | null). The authoritative signal — set the moment
     *  generation is enqueued, so it gates Save on reopen and on the first render
     *  before the builder has reported `generating` up. Null for legacy assets. */
    draftGenerationStatus: string | null
}

export interface SaveAsVersionState {
    saveDisabled: boolean
    saveDisabledReason: string | undefined
    latestVersion: AssetVersionViewDto | null
    draftAheadOfVersion: boolean
}

/**
 * Parse a server timestamp to epoch millis, always as UTC. Every timestamp the API
 * emits is UTC, but they don't all serialize with a zone designator: a Landing
 * Page's fresh `DraftUpdatedAt` comes back from the apply/POST as `…Z` (Kind=Utc),
 * while a version's `UploadedAt` / `CurrentVersionUploadedAt` is re-read from Cosmos
 * and comes back zone-less. `new Date('…')` on a zone-less string is parsed in the
 * VIEWER's local time, so in a UTC-behind timezone (e.g. US Eastern) that value
 * lands hours in the future — making a just-changed draft look OLDER than its last
 * saved version and wrongly leaving "Save as Version" disabled after an instant
 * font change. (A chat rebuild dodges this only because both sides come back in the
 * same format.) Pin zone-less values to UTC so the comparison is timezone-safe.
 */
function parseServerTimestamp(value: string | null | undefined): number | null {
    if (!value) return null
    const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value)
    const ms = new Date(hasZone ? value : `${value}Z`).getTime()
    return Number.isNaN(ms) ? null : ms
}

export function computeSaveAsVersionState({
    assetType,
    lpReadyForVersion,
    emailCopyTemplateAssetId,
    versions,
    currentVersionUploadedAt,
    draftUpdatedAt,
    generating,
    draftGenerationStatus,
}: SaveAsVersionStateInput): SaveAsVersionState {
    let saveDisabled = assetType === 'Landing Page' && !lpReadyForVersion
    let saveDisabledReason = saveDisabled ? 'Generate the thank you page before saving as a version' : undefined

    // Email Copy: require a bound template before allowing a version snapshot.
    if (assetType === 'Email Copy' && !emailCopyTemplateAssetId) {
        saveDisabled = true
        saveDisabledReason = 'Pick a template before saving as a version'
    }

    const latestVersion = versions.length > 0
        ? versions.reduce((a, b) => a.VersionNumber > b.VersionNumber ? a : b)
        : null
    const versionAnchor = currentVersionUploadedAt ?? latestVersion?.UploadedAt ?? null
    // Compare as UTC — see parseServerTimestamp. Using `new Date()` directly here
    // misreads a zone-less version timestamp as local time and breaks the gate.
    const draftMs = parseServerTimestamp(draftUpdatedAt)
    const anchorMs = parseServerTimestamp(versionAnchor)
    const draftAheadOfVersion = !!(latestVersion && draftMs !== null && anchorMs !== null
        && draftMs > anchorMs)

    if (latestVersion && !draftAheadOfVersion) {
        saveDisabled = true
        saveDisabledReason = 'No changes since the latest version'
    }

    // Generation in flight wins over the above messages — initial auto-generation OR a
    // chat-driven rebuild, by either the live builder signal (`generating`) or the server's
    // persisted status (`draftGenerationStatus`, which covers reopen and the first render
    // before the builder reports up). The draft is mid-write; freezing it as a version now
    // would snapshot a partial/placeholder state. Applies uniformly to Landing Page /
    // Email Template / Email Copy — this is the single shared gate for all three.
    if (generating || draftGenerationStatus === 'Generating') {
        saveDisabled = true
        saveDisabledReason = 'Webster is still working — save will be available when it finishes'
    }

    return { saveDisabled, saveDisabledReason, latestVersion, draftAheadOfVersion }
}

// ---------------------------------------------------------------------------
// Save as Version button
// ---------------------------------------------------------------------------

interface SaveAsVersionButtonProps {
    assetType: WebsterAssetType
    promoting: boolean
    onPromoteDraft: (options?: { versionComment?: string }) => Promise<boolean>
    hasLandingPage: boolean
    disabled?: boolean
    disabledReason?: string
    /** Email assets only — flush callback registered by `EmailBuilder` that forces
     *  Unlayer to export its current design + HTML and persist them. Runs before
     *  promote so a freshly Webster-generated asset with null DraftHtml gets compiled
     *  before the server-side promote tries to snapshot it. */
    emailFlushRef?: { current: (() => Promise<void>) | null }
}

export function SaveAsVersionButton({ assetType, promoting, onPromoteDraft, hasLandingPage, disabled, disabledReason, emailFlushRef }: SaveAsVersionButtonProps) {
    const [flushing, setFlushing] = useState(false)

    // For LP, only show once LP entity exists (post-generation). Gate runs after the hook
    // call so the hook count stays stable across the hasLandingPage flip.
    if (assetType === 'Landing Page' && !hasLandingPage) return null

    const handleClick = async () => {
        const flush = emailFlushRef?.current
        if (flush) {
            setFlushing(true)
            try {
                await flush()
            } finally {
                setFlushing(false)
            }
        }
        await onPromoteDraft()
    }

    const inFlight = promoting || flushing
    const isDisabled = inFlight || disabled

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={isDisabled}
            title={disabled ? disabledReason : undefined}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
            {inFlight ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save as Version
        </button>
    )
}

// ---------------------------------------------------------------------------
// External-mode notice — replaces the LP builder when the LP has been bypassed
// ---------------------------------------------------------------------------

export function ExternalModeNotice({ url }: { url?: string | null }) {
    return (
        <div className="h-full w-full flex items-center justify-center p-8">
            <div className="max-w-md w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 text-center space-y-4">
                <div className="mx-auto w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                    <Globe className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                    This landing page uses an external URL
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                    The AI builder is disabled while this campaign is in External mode. Switch back to Builder from the Landing Page tab to edit a built page.
                </p>
                {url && (
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 max-w-full truncate"
                    >
                        <ExternalLink className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                        <span className="truncate">{url}</span>
                    </a>
                )}
            </div>
        </div>
    )
}
